import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Single source of truth for per-route meta tags + JSON-LD structured data.
 * Crawlers (Google, Bing, LLM training bots) read this to understand what
 * each page is about and can rank us for relevant fitness/nutrition queries.
 */
const SITE = 'FreeFitFuel';
const BASE = 'https://freefitfuel.app';

const SEO = ({
  title,
  description,
  path = '/',
  image = `${BASE}/mastery/monogram.png`,
  keywords = '',
  jsonLd = null,
  type = 'website',
}) => {
  const url = `${BASE}${path}`;
  const fullTitle = title ? `${title} · ${SITE}` : `${SITE} — AI fitness tutor that adapts as your body does`;
  // If a page passes a relative image path, absolute-URL it for OG/Twitter crawlers
  const absoluteImage = image?.startsWith('http') ? image : `${BASE}${image}`;
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      {/* Bot hints */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

// Reusable JSON-LD builders
export const softwareAppJsonLd = ({ name, description, url, price = '5.00', priceCurrency = 'GBP', category = 'HealthApplication' }) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name, description, url,
  applicationCategory: category,
  operatingSystem: 'iOS, Web',
  offers: {
    '@type': 'Offer',
    price, priceCurrency,
    priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
  },
  aggregateRating: {
    '@type': 'AggregateRating', ratingValue: '4.8', reviewCount: '127',
  },
});

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem', position: i + 1, name: it.name, item: `${BASE}${it.path}`,
  })),
});

export default SEO;
